// Sales History grouping (FIG-325 / REQ-050): one row per year, combining
// sources; a year with several sources can be expanded to one row per source.

export const SALES_HISTORY_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export type SalesHistoryRow = Record<string, unknown> & {
  year?: string | number | null;
  data_source?: string | null;
  connection_id?: string | null;
};

export interface SalesHistorySourceRow {
  source: string;
  connectionId?: string;
  months: Record<string, number>;
  total: number;
}

export interface SalesHistoryYear {
  year: string;
  months: Record<string, number>;
  total: number;
  sources: SalesHistorySourceRow[];
}

/** Amounts arrive as numbers or formatted strings ("1,234.56"). */
export function parseAmount(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/[,$\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function rowTotal(row: SalesHistoryRow): number {
  if (row.TotalRaw !== undefined && row.TotalRaw !== null && row.TotalRaw !== '') return parseAmount(row.TotalRaw);
  return parseAmount(row.Total);
}

const emptyMonths = () => Object.fromEntries(SALES_HISTORY_MONTHS.map((m) => [m, 0])) as Record<string, number>;

export function groupSalesHistoryByYear(rows: SalesHistoryRow[]): SalesHistoryYear[] {
  const byYear = new Map<string, SalesHistoryYear>();
  for (const row of rows) {
    const year = row.year == null ? '' : String(row.year);
    if (!year) continue;
    let entry = byYear.get(year);
    if (!entry) {
      entry = { year, months: emptyMonths(), total: 0, sources: [] };
      byYear.set(year, entry);
    }
    const months = emptyMonths();
    for (const m of SALES_HISTORY_MONTHS) {
      months[m] = parseAmount(row[m]);
      entry.months[m] += months[m];
    }
    const total = rowTotal(row);
    entry.total += total;
    const source = row.data_source ? String(row.data_source) : 'All sources';
    const connectionId = row.connection_id ? String(row.connection_id) : undefined;
    // One entry per connection: a test clone can return the same connection twice
    // for a year (inherited data and clone-added data).
    const existing = entry.sources.find((s) => (connectionId ? s.connectionId === connectionId : !s.connectionId && s.source === source));
    if (existing) {
      for (const m of SALES_HISTORY_MONTHS) existing.months[m] += months[m];
      existing.total += total;
    } else {
      entry.sources.push({ source, connectionId, months, total });
    }
  }
  return [...byYear.values()];
}

/** Unit counts (∑ Quantity): "1,200". */
export function formatWholeNumber(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

/** Whole dollars with the $ in the cell (REQ-050, REQ-060): "$1,235", "-$40". */
export function formatWholeDollars(value: number): string {
  const rounded = Math.round(value);
  const formatted = `$${Math.abs(rounded).toLocaleString('en-US')}`;
  return rounded < 0 ? `-${formatted}` : formatted;
}
